/**
 * Labstep
 *
 * @module components/Metadata/Card
 * @desc Metadata Card
 */

import { MetadataActionMenu } from 'labstep-web/components/Metadata/Action/Menu';
import { MetadataFormShowEditValueWithClear } from 'labstep-web/components/Metadata/Form/ShowEdit/Value/WithClear';
import { MetadataRequired } from 'labstep-web/components/Metadata/Required';
import { MetadataThreadContainer as MetadataThreadComponent } from 'labstep-web/components/Metadata/Thread';
import { ProtocolConditionSmartTableModal } from 'labstep-web/components/ProtocolCondition/SmartTable/Modal';
import { ICONS } from 'labstep-web/constants/icons';
import ProtocolChildRouter from 'labstep-web/containers/Router/ProtocolChild';
import { ActionComponent } from 'labstep-web/core/Action/Component';
import { ElementCard } from 'labstep-web/core/Card/Element';
import { Flex } from 'labstep-web/core/Flex';
import { GridSmartTableProtocolConditionService } from 'labstep-web/grid/SmartTableProtocolCondition/services/grid-smart-table-protocol-condition.service';
import { useActiveGroup } from 'labstep-web/hooks/activeGroup';
import {
  ENTITY_VIEW_DEFAULT_PARAMETERS,
  EntityView,
} from 'labstep-web/models/entity-view.model';
import { Experiment } from 'labstep-web/models/experiment.model';
import { METADATA_DEFAULT_LABEL_DATA } from 'labstep-web/models/metadata/constants';
import { ProtocolCondition } from 'labstep-web/models/protocol-condition.model';
import type React from 'react';
import type { MetadataCardProps } from './types';

export const MetadataCard: React.FC<MetadataCardProps> = ({
  metadata,
  entity,
  isTemplate,
  additionalActions,
  hideActions,
  dragHandle,
  isInput,
}) => {
  const activeGroup = useActiveGroup();

  const defaultEntityView = new EntityView({
    group: activeGroup.activeGroup!,
    context: 'smart_table',
    entity_name: ProtocolCondition.entityName,
    column_definition_ids:
      GridSmartTableProtocolConditionService.getDefaultVariableColIds(
        entity,
      ),
    parameters: ENTITY_VIEW_DEFAULT_PARAMETERS,
  });

  return (
    <ProtocolChildRouter
      route={isInput ? 'metadata_show' : 'metadata_show_output'}
      id={metadata.id}
    >
      {({ navigate }): React.ReactElement => (
        <ElementCard
          onClick={navigate}
          icon={ICONS.metadata.type[metadata.type]}
          name={metadata.label || METADATA_DEFAULT_LABEL_DATA}
          detail={
            <div>
              {metadata.is_variable ? (
                <ProtocolConditionSmartTableModal
                  experimentOrProtocol={entity}
                  entityView={defaultEntityView}
                  viewComponent={
                    <ActionComponent
                      type="text"
                      icon="external"
                      text={`${entity.n_protocol_conditions} ${ProtocolCondition.getHumanReadableEntityName(true)}`}
                    />
                  }
                />
              ) : (
                <MetadataFormShowEditValueWithClear
                  key={metadata.type}
                  entity={entity}
                  metadata={metadata}
                />
              )}
            </div>
          }
          actions={
            !hideActions && (
              <Flex
                data-private
                wrap
                style={{ alignItems: 'baseline' }}
                data-cy="components/Metadata/Card"
              >
                <div style={{ width: 10 }} />
                <MetadataRequired metadata={metadata} />
                {!isTemplate && entity instanceof Experiment && (
                  <>
                    <div style={{ width: 10 }} />
                    <MetadataThreadComponent metadata={metadata} />
                  </>
                )}
                <MetadataActionMenu
                  entity={entity}
                  metadata={metadata}
                  actions={[
                    'edit',
                    'delete',
                    'reporting',
                    'add_from_relation',
                  ]}
                />
                <div>{additionalActions}</div>
              </Flex>
            )
          }
          dragHandle={dragHandle}
        />
      )}
    </ProtocolChildRouter>
  );
};
